import { Button as BaseButton, ButtonProps as BaseButtonProps, forwardRef } from '@chakra-ui/react';
import { ReactElement, ReactNode } from 'react';

import { rem } from '@bonusly/ds-utils';

import { useVariantStyle } from '../hooks/use-variant-style';
import { ButtonColorScheme, ButtonSize, ButtonVariant } from '../types';

type SafeButtonProps = Omit<BaseButtonProps, 'colorScheme' | 'size'>;

export interface ButtonProps extends SafeButtonProps {
	colorScheme?: ButtonColorScheme;
	size?: ButtonSize;
	variant?: ButtonVariant;
}

// Internal props for handling legacy icon prop - not exported to avoid type conflicts
interface InternalButtonProps extends ButtonProps {
	icon?: ReactNode; // Backward compatibility - treated as leftIcon
}

/**
 * Icon dimensions per size
 */
const ICON_SIZES: Record<ButtonSize, string> = {
	xs: rem(16),
	sm: rem(16),
	md: rem(20),
	lg: rem(20),
	xl: rem(24),
};

/**
 * Spacing configuration per size following Button 2.0 design spec
 *
 * - button: outer padding around button content (when icon present on that side)
 * - gap: space between icon and text
 * - text: additional padding around text for breathing room
 * - total: total padding when no icon is present on that side
 */
const SPACING_CONFIG: Record<ButtonSize, { button: number; gap: number; text: number; total: number }> = {
	xs: { button: 6, gap: 2, text: 2, total: 8 },
	sm: { button: 10, gap: 2, text: 2, total: 12 },
	md: { button: 14, gap: 4, text: 2, total: 16 },
	lg: { button: 14, gap: 2, text: 4, total: 18 },
	xl: { button: 16, gap: 2, text: 4, total: 20 },
};

/**
 * Ghost variant spacing configuration - uses reduced button padding
 */
const GHOST_SPACING_CONFIG: Record<ButtonSize, { button: number; gap: number; text: number; total: number }> = {
	xs: { button: 2, gap: 2, text: 2, total: 6 },
	sm: { button: 4, gap: 2, text: 2, total: 8 },
	md: { button: 6, gap: 4, text: 2, total: 12 },
	lg: { button: 8, gap: 2, text: 4, total: 14 },
	xl: { button: 8, gap: 2, text: 4, total: 14 },
};

/**
 * Gets the appropriate spacing config based on variant
 */
const getSpacingConfig = (size: ButtonSize, variant: ButtonVariant) => {
	return variant === 'ghost' ? GHOST_SPACING_CONFIG[size] : SPACING_CONFIG[size];
};

/**
 * Calculates horizontal padding based on icon presence.
 *
 * When an icon is present on a side, uses base button padding.
 * When no icon, includes gap and text padding for proper spacing.
 */
const calculatePadding = (hasIcon: boolean, config: (typeof SPACING_CONFIG)[ButtonSize]): string => {
	return hasIcon ? rem(config.button) : rem(config.total);
};

/**
 * Calculates the gap between icons and text.
 *
 * Gap combines the design spec gap with text padding to create
 * breathing room around text content while keeping icons close to edges.
 */
const calculateGap = (hasIcons: boolean, config: (typeof SPACING_CONFIG)[ButtonSize]): string => {
	return hasIcons ? rem(config.gap + config.text) : rem(0);
};

const Button = forwardRef(
	({ children, colorScheme = 'primary', icon, leftIcon, rightIcon, size = 'md', variant = 'solid', pl, pr, p, ...rest }: InternalButtonProps, ref) => {
		const style = useVariantStyle({ colorScheme, size, variant });

		// Backward compatibility: treat icon prop as leftIcon
		const resolvedLeftIcon = (leftIcon || icon) as ReactElement | undefined;

		const config = getSpacingConfig(size, variant);
		const hasIcons = Boolean(resolvedLeftIcon || rightIcon);

		// Link variant should not have dynamic padding (inline text style)
		// but still needs gap for icon spacing
		const shouldApplyPadding = variant !== 'link';
		const shouldApplyGap = true; // Always apply gap, even for link variant

		// Only apply dynamic padding if pl/pr/p are not explicitly provided
		// This allows components to override padding when needed (e.g., TagButton)
		// Check each side independently so one side can be overridden while the other uses dynamic padding
		const shouldApplyDynamicPl = shouldApplyPadding && pl === undefined && p === undefined;
		const shouldApplyDynamicPr = shouldApplyPadding && pr === undefined && p === undefined;

		// Only apply `p` (all padding) if neither pl nor pr are explicitly provided
		// This prevents `p` from overriding explicit left/right padding values
		const shouldApplyP = p !== undefined && pl === undefined && pr === undefined;

		return (
			<BaseButton
				ref={ref}
				display="flex"
				alignItems="center"
				fontFamily="ui"
				fontWeight="medium"
				leftIcon={resolvedLeftIcon}
				rightIcon={rightIcon}
				sx={{
					'.chakra-button__icon': {
						width: ICON_SIZES[size],
						height: ICON_SIZES[size],
						fontSize: ICON_SIZES[size],
						margin: 0,
						display: 'inline-flex',
						alignItems: 'center',
						justifyContent: 'center',
					},
					'.chakra-button__icon > svg': {
						width: ICON_SIZES[size],
						height: ICON_SIZES[size],
					},
				}}
				{...style}
				{...(shouldApplyP && { p })}
				{...(shouldApplyDynamicPl && {
					pl: calculatePadding(!!resolvedLeftIcon, config),
				})}
				{...(shouldApplyDynamicPr && {
					pr: calculatePadding(!!rightIcon, config),
				})}
				{...(shouldApplyGap && {
					gap: calculateGap(hasIcons, config),
				})}
				{...(pl !== undefined && { pl })}
				{...(pr !== undefined && { pr })}
				{...rest}
			>
				{children}
			</BaseButton>
		);
	}
);

Button.displayName = 'Button';

export { Button };
